import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PushedDeal } from 'src/app/models/pushed-deal';
import { NotificationService } from 'src/app/services/notifications.service';
import { I18nService } from 'src/app/services/i18n.service';

// Cloche du header mobile : badge des deals notifiés non lus + liste des dernières notifications.
// L'activation du push reste gérée par le header (toggleNotifications), via togglePush.
@Component({
  selector: 'app-notifications-bell',
  templateUrl: './notifications-bell.component.html',
  styleUrls: ['./notifications-bell.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NotificationsBellComponent implements OnInit {

  private static readonly LAST_SEEN_KEY = 'notificationsLastSeen';
  private static readonly HISTORY_LIMIT = 20;

  @Input() pushSupported = false;
  @Input() pushEnabled = false;
  @Input() pushBusy = false;
  @Output() togglePush = new EventEmitter<void>();

  deals: PushedDeal[] = [];
  unread = 0;
  // Date "vue" au moment de l'ouverture : garde la mise en évidence des nouveautés tant que la liste est ouverte
  private highlightAfter = 0;
  private destroyRef = inject(DestroyRef);

  constructor(
    private notificationService: NotificationService,
    private swPush: SwPush,
    private router: Router,
    private i18n: I18nService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.load();
    // Nouvelle notification reçue pendant que le site est ouvert
    if (this.swPush.isEnabled) {
      this.swPush.messages.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load());
    }
    // Retour sur l'onglet / l'application : des deals ont pu être poussés entre-temps
    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
  }

  get badge(): string {
    return this.unread > 9 ? '9+' : String(this.unread);
  }

  load(): void {
    this.notificationService.history(NotificationsBellComponent.HISTORY_LIMIT).subscribe({
      next: deals => {
        this.deals = deals || [];
        const lastSeen = this.lastSeen();
        this.unread = this.deals.filter(d => this.time(d) > lastSeen).length;
      },
      error: err => console.error('Could not load notifications:', err)
    });
  }

  onOpened(): void {
    this.highlightAfter = this.lastSeen();
    if (this.deals.length > 0) {
      this.saveLastSeen(Math.max(...this.deals.map(d => this.time(d))));
    }
    this.unread = 0;
  }

  isNew(deal: PushedDeal): boolean {
    return this.time(deal) > this.highlightAfter;
  }

  openDeal(deal: PushedDeal): void {
    this.router.navigate(['/product', deal._id]);
  }

  isPromoCode(deal: PushedDeal): boolean {
    return deal.dealType === 'promotionCode';
  }

  // priceDiscount = prix actuel, price = ancien prix (barré), comme sur les cartes du dashboard
  currentPrice(deal: PushedDeal): number {
    return deal.priceDiscount || deal.price;
  }

  oldPrice(deal: PushedDeal): number | null {
    return deal.priceDiscount && deal.price > deal.priceDiscount ? deal.price : null;
  }

  timeAgo(deal: PushedDeal): string {
    const seconds = Math.round((this.time(deal) - Date.now()) / 1000);
    const units: [Intl.RelativeTimeFormatUnit, number][] = [['day', 86400], ['hour', 3600], ['minute', 60]];
    const format = new Intl.RelativeTimeFormat(this.i18n.current, { numeric: 'auto' });
    for (const [unit, size] of units) {
      if (Math.abs(seconds) >= size) {
        return format.format(Math.round(seconds / size), unit);
      }
    }
    return format.format(0, 'minute');
  }

  private time(deal: PushedDeal): number {
    return new Date(deal.pushSentAt).getTime() || 0;
  }

  private lastSeen(): number {
    try {
      return Number(localStorage.getItem(NotificationsBellComponent.LAST_SEEN_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  private saveLastSeen(value: number): void {
    try {
      localStorage.setItem(NotificationsBellComponent.LAST_SEEN_KEY, String(value));
    } catch {
      // stockage indisponible (navigation privée) : le badge reviendra au prochain chargement
    }
  }
}
